import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import Spinner from '../components/Spinner';

const StaffQuizDetail: React.FC = () => {
  const { quizId, date } = useParams<{ quizId: string; date: string }>();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<any | null>(null);
  // Remove editScore, add editAnswers for per-question editing
  const [editAnswers, setEditAnswers] = useState<any[]>([]);
  const [editJustify, setEditJustify] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchSubmissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/staff/quiz-submissions', {
        params: { quizId, date }
      });
      // Filter by date (createdAt)
      const filtered = res.data.filter((s: any) => new Date(s.createdAt).toISOString().split('T')[0] === date);
      setSubmissions(filtered);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load quiz submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line
  }, [quizId, date]);

  const handleRowClick = (sub: any) => {
    setSelected(sub);
    // Deep copy answers for editing
    setEditAnswers(sub.answers ? sub.answers.map((a: any) => ({ ...a })) : []);
    setEditJustify(sub.scoreEditJustification || '');
    setEditError(null);
    setEditSuccess(null);
  };

  const handleSave = async () => {
    // Validate all question scores
    for (const ans of editAnswers) {
      if (ans.pointsAwarded === '' || isNaN(Number(ans.pointsAwarded))) {
        setEditError('All question scores are required and must be numbers');
        return;
      }
      if (Number(ans.pointsAwarded) < 0) {
        setEditError('Scores cannot be negative');
        return;
      }
      if (Number(ans.pointsAwarded) > ans.maxPoints) {
        setEditError(`Score for a question cannot exceed its max points (${ans.maxPoints})`);
        return;
      }
    }
    if (!editJustify.trim()) {
      setEditError('Justification is required');
      return;
    }
    setEditLoading(true);
    setEditError(null);
    try {
      const response = await api.put(`/staff/quiz-submissions/${selected._id}/grade`, {
        grades: editAnswers.map((a: any) => ({
          questionId: a.questionId,
          pointsAwarded: Number(a.pointsAwarded),
        })),
        feedback: selected.feedback || '',
        scoreEditJustification: editJustify,
      });
      const updatedSubmission = response.data?.submission;
      setEditSuccess('Scores updated successfully');
      await fetchSubmissions();
      if (updatedSubmission) {
        setSelected(updatedSubmission);
        setEditAnswers(updatedSubmission.answers ? updatedSubmission.answers.map((a: any) => ({ ...a })) : []);
        setEditJustify(updatedSubmission.scoreEditJustification || '');
      }
    } catch (err: any) {
      setEditError(err?.response?.data?.message || 'Failed to update scores');
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 4 }, minHeight: '80vh' }}>
      <Button variant="outlined" sx={{ mb: 2 }} onClick={() => navigate(-1)}>
        Back
      </Button>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Quiz Submissions for {date}
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <Spinner />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3, color: 'error.main', background: '#fff0f0' }}>{error}</Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Student Name</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Total Score</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Justification</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((sub) => (
                <TableRow key={sub._id} hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(sub)}>
                  <TableCell>{sub.student?.name || 'N/A'}</TableCell>
                  <TableCell>{sub.score}</TableCell>
                  <TableCell>{sub.totalPoints}</TableCell>
                  <TableCell>{sub.status}</TableCell>
                  <TableCell>{new Date(sub.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{sub.scoreEditJustification || ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Quiz Submission Details</DialogTitle>
        <DialogContent>
          {selected && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}><b>Student:</b> {selected.student?.name || 'N/A'}</Typography>
              <Typography variant="subtitle1" sx={{ mb: 1 }}><b>Quiz:</b> {selected.quiz?.title || 'N/A'}</Typography>
              <Typography variant="subtitle1" sx={{ mb: 1 }}><b>Status:</b> {selected.status}</Typography>
              <Typography variant="subtitle1" sx={{ mb: 1 }}><b>Date:</b> {new Date(selected.createdAt).toLocaleString()}</Typography>
              <Typography variant="subtitle1" sx={{ mb: 1 }}><b>Score:</b> {selected.score} / {selected.totalPoints}</Typography>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Edit Per-Question Scores:</Typography>
              {editAnswers.map((ans, idx) => (
                <Box key={ans.questionId || idx} sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 500 }}>{`Q${idx + 1}: ${ans.questionText || ''}`}</Typography>
                  <TextField
                    label={`Score (max ${ans.maxPoints})`}
                    type="number"
                    value={ans.pointsAwarded}
                    onChange={e => {
                      const val = e.target.value;
                      setEditAnswers(prev => prev.map((a, i) => i === idx ? { ...a, pointsAwarded: val === '' ? '' : Math.max(0, Math.min(Number(val), a.maxPoints)) } : a));
                    }}
                    inputProps={{ min: 0, max: ans.maxPoints }}
                    sx={{ mt: 1, width: 120 }}
                  />
                  <Typography variant="caption" sx={{ ml: 2 }}>Max: {ans.maxPoints}</Typography>
                </Box>
              ))}
              <TextField
                label="Justify Score Edit"
                value={editJustify}
                onChange={e => setEditJustify(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 2 }}
              />
              {editError && <Alert severity="error" sx={{ mt: 2 }}>{editError}</Alert>}
              {editSuccess && <Alert severity="success" sx={{ mt: 2 }}>{editSuccess}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} disabled={editLoading}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={editLoading}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffQuizDetail;
